import React, { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';
import { Icon } from '@packages/ui/shared';
import Modal from 'react-bootstrap/Modal';
import { useParams } from 'react-router-dom';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { analyticsService } from '@web/services/singletons';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics';
import { useGoogleAnalytics } from '@web/core/google-analytics/use-google-analytics';
import { PdfHighlighter } from '../stis-publications/pdf-highlighter';
import { usePublicationsDispatch } from '../stis-publications/context/publications.hooks';
import { publicationsActions } from '../stis-publications/context/publications.state';
import { useVinSessionState } from '../../context/vin-sessions.hooks';
import { QUICK_REFERENCE_TABS, QuickReferenceTab } from './quick-reference-tabs';
import { QUICK_REFERENCE_CARD_WIDTH, QuickReferenceCard } from './quick-reference-card';
import { QuickReferenceCardSkeleton } from './quick-reference-skeleton';
import { QUICK_REFERENCE_PAGE_SIZE, useQuickReferences } from './use-quick-references';

const LOAD_MORE_THRESHOLD_PX = 400;

const SKELETON_CARD_COUNT = QUICK_REFERENCE_PAGE_SIZE;

const CARD_STEP_PX = QUICK_REFERENCE_CARD_WIDTH + 16;

const useStyles = createUseStyles({
    heading: {
        marginBottom: 16,
    },
    // The count sits beside the tablist, not inside it: a tablist may only contain tabs.
    tabBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '0 16px',
        borderBottom: `1px solid ${colors.grayThree}`,
    },
    count: {
        flex: '0 0 auto',
        fontSize: 12,
        color: colors.graySeven,
        whiteSpace: 'nowrap',
    },
    panel: {
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 8,
        backgroundColor: colors.white,
        overflow: 'hidden',
    },
    tabs: {
        display: 'flex',
        gap: 8,
        minWidth: 0,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
    },
    tab: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        // Tight because each glyph carries ~3px of empty viewBox per side, so 8px reads as 11.
        gap: 4,
        flex: '0 0 auto',
        padding: '14px 8px',
        // Normalised because a bare inline <svg> pads the icon box with baseline descender.
        '& > div, & > svg': {
            display: 'flex',
            flex: '0 0 auto',
        },
        border: 'none',
        background: 'none',
        fontSize: 14,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: colors.grayFive,
        cursor: 'pointer',
        transition: 'color 160ms ease',
        '&::after': {
            content: '""',
            position: 'absolute',
            left: 8,
            right: 8,
            bottom: -1,
            height: 2,
            borderRadius: 2,
            backgroundColor: colors.blueOne,
            transform: 'scaleX(0)',
            transition: 'transform 200ms ease',
        },
        '&:hover': {
            color: colors.blueOneHover,
        },
        '&:focus-visible': {
            outline: `2px solid ${colors.blueOne}`,
            outlineOffset: -2,
        },
        '@media (prefers-reduced-motion: reduce)': {
            '&::after': { transition: 'none' },
        },
    },
    tabActive: {
        color: colors.blueOne,
        '&::after': {
            transform: 'scaleX(1)',
        },
    },
    carousel: {
        position: 'relative',
    },
    strip: {
        display: 'flex',
        gap: 16,
        padding: 24,
        overflowX: 'auto',
        scrollBehavior: 'smooth',
        scrollSnapType: 'x mandatory',
        // Inset the snapport by the same amount as the padding. Without this, mandatory
        // snapping aligns the first card to the scrollport edge rather than the padding edge,
        // so the strip rests at scrollLeft 24 instead of 0 and the left arrow shows on load.
        // Written with the unit: JSS only appends px for properties in its own units map, and
        // scroll-padding is not one of them, so a bare 24 emits invalid unitless CSS.
        scrollPaddingInline: '24px',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        '@media (prefers-reduced-motion: reduce)': {
            scrollBehavior: 'auto',
        },
    },
    fade: {
        position: 'absolute',
        top: 1,
        bottom: 1,
        width: 72,
        pointerEvents: 'none',
        zIndex: 1,
    },
    fadeLeft: {
        left: 0,
        background: `linear-gradient(to right, ${colors.white}, rgba(255, 255, 255, 0))`,
    },
    fadeRight: {
        right: 0,
        background: `linear-gradient(to left, ${colors.white}, rgba(255, 255, 255, 0))`,
    },
    arrow: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 2,
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: `1px solid ${colors.grayThree}`,
        backgroundColor: colors.white,
        boxShadow: '0 2px 10px rgba(7, 20, 36, 0.15)',
        cursor: 'pointer',
        transition: 'background-color 160ms ease, border-color 160ms ease',
        '&:hover': {
            backgroundColor: colors.grayOne,
            borderColor: colors.blueTwo,
        },
        '&:focus-visible': {
            outline: `2px solid ${colors.blueOne}`,
            outlineOffset: 2,
        },
    },
    arrowLeft: { left: 8 },
    arrowRight: { right: 8 },
    message: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: '48px 24px',
        textAlign: 'center',
    },
    messageTitle: {
        margin: 0,
        fontSize: 15,
        fontWeight: 600,
        color: colors.textDarkPrimary,
    },
    messageBody: {
        margin: 0,
        fontSize: 13,
        color: colors.textDarkSecondary,
    },
    retry: {
        marginTop: 8,
        padding: '6px 16px',
        fontSize: 13,
        fontWeight: 600,
        color: colors.blueOne,
        backgroundColor: colors.white,
        border: `1px solid ${colors.blueOne}`,
        borderRadius: 4,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: colors.blueFour,
        },
    },
});

// Quick References carry no MIME type, so the S3 key is the only signal. Keyed on the file
// rather than the tab, so a non-PDF filed under TSBs is still judged correctly.
const isPreviewableDocument = (url: string | null): boolean => {
    if (!url) {
        return false;
    }
    const path = url.split('?')[0];
    return path.toLowerCase().endsWith('.pdf');
};

const NO_HIGHLIGHTS: string[] = [];

// The URL carries Content-Disposition: attachment, so an anchor click saves the file without
// navigating the page and without tripping the pop-up blocker window.open would.
const triggerDownload = (url: string) => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
};

export const QuickReferences: FC = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { vin } = useParams<{ vin: string }>();
    const { selectedVin } = useVinSessionState();
    const { googleAnalyticsService } = useGoogleAnalytics();
    const publicationDispatch = usePublicationsDispatch();
    const stripRef = useRef<HTMLDivElement>(null);

    const [activeTab, setActiveTab] = useState<QuickReferenceTab>(QUICK_REFERENCE_TABS[0]);
    const activePublicationType = activeTab.publicationType;
    const [overflow, setOverflow] = useState({ left: false, right: false });
    const [preview, setPreview] = useState<{ url: string; title: string } | null>(null);

    const carlineId = selectedVin?.carlineId;
    const modelYear = selectedVin?.vinRecord.modelYear;

    const { tab, loadMore, retry, resolveDownloadUrl } = useQuickReferences({
        activePublicationType,
        carlineId,
        modelYear,
    });

    const syncOverflow = useCallback(() => {
        const strip = stripRef.current;
        if (!strip) {
            return;
        }
        const { scrollLeft, scrollWidth, clientWidth } = strip;
        setOverflow({
            left: scrollLeft > 1,
            right: scrollLeft + clientWidth < scrollWidth - 1,
        });
    }, []);

    const handleScroll = useCallback(() => {
        syncOverflow();

        const strip = stripRef.current;
        if (!strip) {
            return;
        }
        const remaining = strip.scrollWidth - strip.scrollLeft - strip.clientWidth;
        if (remaining < LOAD_MORE_THRESHOLD_PX) {
            loadMore();
        }
    }, [syncOverflow, loadMore]);

    useLayoutEffect(syncOverflow, [syncOverflow, tab.documents, tab.status]);

    useEffect(() => {
        window.addEventListener('resize', syncOverflow);
        return () => window.removeEventListener('resize', syncOverflow);
    }, [syncOverflow]);

    useEffect(() => {
        if (stripRef.current) {
            stripRef.current.scrollLeft = 0;
        }
        setPreview(null);
    }, [activePublicationType]);

    const scrollByPage = (direction: -1 | 1) => {
        const strip = stripRef.current;
        if (!strip) {
            return;
        }
        const distance = Math.max(strip.clientWidth - CARD_STEP_PX, CARD_STEP_PX);
        // Passing behavior explicitly overrides the stylesheet, so the reduced-motion
        // preference has to be read here too rather than left to the CSS rule on the strip.
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        strip.scrollBy({ left: direction * distance, behavior: reducedMotion ? 'auto' : 'smooth' });
    };

    const handleTabKeyDown = (event: React.KeyboardEvent) => {
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (step === 0) {
            return;
        }
        event.preventDefault();

        const current = QUICK_REFERENCE_TABS.findIndex((config) => config.publicationType === activePublicationType);
        const next = QUICK_REFERENCE_TABS[(current + step + QUICK_REFERENCE_TABS.length) % QUICK_REFERENCE_TABS.length];
        setActiveTab(next);
        document.getElementById(`quick-reference-tab-${next.publicationType}`)?.focus();
    };

    const reportFailure = (documentTitle: string) => {
        analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_OPEN_FAILED, {
            screen: 'vin_session_dashboard',
            source: 'quick_references',
            docType: activePublicationType,
            type: activePublicationType,
            documentTitle,
            vin,
            vinPresent: !!vin,
        });
        googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentOpen, {
            success: false,
            entry_point: 'quick_references',
            document_type: activePublicationType,
        });
        publicationDispatch(
            publicationsActions.setToastMessage({
                toast: { toastMessage: 'Unable to open the publication', iconColor: 'white', iconName: 'x-close' },
            })
        );
    };

    const handleDownload = async (index: number) => {
        const reference = tab.documents[index];
        const url = await resolveDownloadUrl(index);
        if (!url || !reference) {
            reportFailure(reference?.documentTitle ?? '');
            return;
        }
        triggerDownload(url);
        analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_DOWNLOADED, {
            screen: 'vin_session_dashboard',
            source: 'quick_references',
            docType: activePublicationType,
            type: activePublicationType,
            documentTitle: reference.documentTitle,
            vin,
            vinPresent: !!vin,
        });
    };

    const handlePreview = async (index: number) => {
        const reference = tab.documents[index];
        const url = await resolveDownloadUrl(index);
        if (!url || !reference) {
            reportFailure(reference?.documentTitle ?? '');
            return;
        }
        setPreview({ url, title: reference.documentTitle });
        analyticsService.logEvent(ANALYTICS_EVENTS.STIS_DOCUMENT_PREVIEWED, {
            screen: 'vin_session_dashboard',
            source: 'quick_references',
            docType: activePublicationType,
            type: activePublicationType,
            documentTitle: reference.documentTitle,
            vin,
            vinPresent: !!vin,
        });
        googleAnalyticsService.track(GoogleAnalyticsEvents.DocumentOpen, {
            success: true,
            entry_point: 'quick_references',
            document_type: activePublicationType,
        });
    };

    const isFirstLoad = tab.status === 'loading' || tab.status === 'idle';
    const isEmpty = tab.status === 'ready' && tab.documents.length === 0;

    return (
        <div data-testid="quick-references">
            <div className={classes.heading}>
                <h3 className="mb-0">{t('Quick References')}</h3>
            </div>

            <div className={classes.panel}>
                <div className={classes.tabBar}>
                    <div
                        className={classes.tabs}
                        role="tablist"
                        aria-label={t('Quick reference categories')}
                        onKeyDown={handleTabKeyDown}
                    >
                        {QUICK_REFERENCE_TABS.map((config) => {
                            const isActive = config.publicationType === activePublicationType;
                            return (
                                <button
                                    key={config.publicationType}
                                    type="button"
                                    role="tab"
                                    id={`quick-reference-tab-${config.publicationType}`}
                                    aria-controls="quick-reference-panel"
                                    aria-selected={isActive}
                                    tabIndex={isActive ? 0 : -1}
                                    className={`${classes.tab} ${isActive ? classes.tabActive : ''}`}
                                    onClick={() => setActiveTab(config)}
                                >
                                    <config.Icon size={18} color={isActive ? colors.blueOne : colors.grayFive} />
                                    {t(config.label)}
                                </button>
                            );
                        })}
                    </div>

                    {tab.totalElements > 0 && (
                        <span className={classes.count}>
                            {`${tab.totalElements} ${tab.totalElements === 1 ? t('document') : t('documents')}`}
                        </span>
                    )}
                </div>

                {tab.status === 'error' && (
                    <div className={classes.message} role="alert">
                        <p className={classes.messageTitle}>{t('Quick references could not be loaded')}</p>
                        <p className={classes.messageBody}>{t('Check your connection and try again.')}</p>
                        <button type="button" className={classes.retry} onClick={retry}>
                            {t('Try again')}
                        </button>
                    </div>
                )}

                {isEmpty && (
                    <div className={classes.message}>
                        <p className={classes.messageTitle}>{t('No quick references for this vehicle')}</p>
                        <p className={classes.messageBody}>
                            {t('Documents published for this model year and carline will appear here.')}
                        </p>
                    </div>
                )}

                {tab.status !== 'error' && !isEmpty && (
                    <div
                        className={classes.carousel}
                        id="quick-reference-panel"
                        role="tabpanel"
                        aria-labelledby={`quick-reference-tab-${activePublicationType}`}
                    >
                        {overflow.left && <div className={`${classes.fade} ${classes.fadeLeft}`} aria-hidden="true" />}
                        {overflow.right && (
                            <div className={`${classes.fade} ${classes.fadeRight}`} aria-hidden="true" />
                        )}

                        {overflow.left && !isFirstLoad && (
                            <button
                                type="button"
                                className={`${classes.arrow} ${classes.arrowLeft}`}
                                aria-label={t('Scroll left')}
                                onClick={() => scrollByPage(-1)}
                            >
                                <Icon name="chevron-left" size={20} color="blueOne" />
                            </button>
                        )}
                        {overflow.right && !isFirstLoad && (
                            <button
                                type="button"
                                className={`${classes.arrow} ${classes.arrowRight}`}
                                aria-label={t('Scroll right')}
                                onClick={() => scrollByPage(1)}
                            >
                                <Icon name="chevron-right" size={20} color="blueOne" />
                            </button>
                        )}

                        <div
                            className={classes.strip}
                            data-testid="quick-reference-strip"
                            ref={stripRef}
                            onScroll={handleScroll}
                        >
                            {isFirstLoad
                                ? Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
                                      <QuickReferenceCardSkeleton key={index} />
                                  ))
                                : tab.documents.map((reference, index) => (
                                      <QuickReferenceCard
                                          key={`${reference.documentTitle}::${reference.version ?? 'no-version'}`}
                                          reference={reference}
                                          TypeIcon={activeTab.Icon}
                                          canPreview={isPreviewableDocument(reference.downloadUrl)}
                                          onPreview={() => handlePreview(index)}
                                          onDownload={() => handleDownload(index)}
                                      />
                                  ))}
                            {tab.status === 'loadingMore' && <QuickReferenceCardSkeleton />}
                        </div>
                    </div>
                )}
            </div>

            {preview && (
                <Modal show onHide={() => setPreview(null)} size="xl" backdrop="static" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{preview.title}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto', padding: 0 }}>
                        <PdfHighlighter
                            documentUrl={preview.url}
                            keyword=""
                            pageNumber={1}
                            highlightedWords={NO_HIGHLIGHTS}
                        />
                    </Modal.Body>
                </Modal>
            )}
        </div>
    );
};
